import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import compligaurdLogo from "@/assets/compligaurd-logo.webp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type NavLinkItem = { label: string; href: string };
type FeaturesGroup = { label: "Features"; children: NavLinkItem[] };
type NavItem = NavLinkItem | FeaturesGroup;

const featuresChildren: NavLinkItem[] = [
  { label: "For Brands", href: "/vds-brands" },
  { label: "For Practitioners", href: "/practitioners" },
];

const navItems: NavItem[] = [
  { label: "Features", children: featuresChildren },
  { label: "How it works", href: "/#how-it-works" },
  { label: "Pricing", href: "/pricing" },
  { label: "FAQ's", href: "/compliance" },
  { label: "Guides", href: "/guides" },
  { label: "Insights", href: "/insights" },
  { label: "About", href: "/about" },
  { label: "Help", href: "/help" },
  { label: "Contact", href: "/contact" },
];

const linkClass =
  "text-text-body text-sm hover:text-primary transition-colors duration-200 relative after:absolute after:bottom-[-2px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-200 hover:after:w-full";

const LandingNav = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
      <div className="container max-w-6xl px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0 group">
          <img src={compligaurdLogo} alt="CompliGUARD — Compliance infrastructure for Australian health brands" className="h-8 w-8 sm:h-[42px] sm:w-[42px] transition-transform duration-200 group-hover:scale-105" />
          <span className="text-foreground text-xl sm:text-[27px] font-bold tracking-wide">
            Compli<span className="gold-text">GUARD</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          {navItems.map((item) => {
            if ("children" in item) {
              return (
                <DropdownMenu key={item.label}>
                  <DropdownMenuTrigger className={`${linkClass} inline-flex items-center gap-1 outline-none`}>
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-card">
                    {item.children.map((child) => (
                      <DropdownMenuItem key={child.label} asChild>
                        <Link to={child.href}>{child.label}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }
            return item.href.startsWith("/#") ? (
              <a key={item.label} href={item.href} className={linkClass}>
                {item.label}
              </a>
            ) : (
              <Link key={item.label} to={item.href} className={linkClass}>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop auth buttons */}
        <div className="hidden lg:flex items-center gap-4">
          <Link to="/login" className="text-text-body text-sm hover:text-primary transition-colors duration-200">Log in</Link>
          <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-accent-hover transition-all duration-200 hover:shadow-md">
            <Link to="/signup">Sign Up</Link>
          </Button>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden text-text-body hover:text-foreground p-1.5 -mr-1.5 transition-colors duration-200"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-card">
          <nav className="container max-w-6xl px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            {navItems.map((item) => {
              if ("children" in item) {
                return (
                  <div key={item.label} className="flex flex-col">
                    <span className="text-text-caption text-[10px] uppercase tracking-widest py-2.5 px-3">
                      {item.label}
                    </span>
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        onClick={() => setMobileOpen(false)}
                        className="text-text-body text-sm hover:text-primary transition-colors duration-200 py-2.5 pl-6 pr-3 rounded-sm hover:bg-secondary active:bg-secondary"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                );
              }
              return item.href.startsWith("/#") ? (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-text-body text-sm hover:text-primary transition-colors duration-200 py-2.5 px-3 rounded-sm hover:bg-secondary active:bg-secondary"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-text-body text-sm hover:text-primary transition-colors duration-200 py-2.5 px-3 rounded-sm hover:bg-secondary active:bg-secondary"
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="border-t border-border pt-3 mt-2 flex flex-col gap-1.5">
              <Link to="/login" onClick={() => setMobileOpen(false)} className="text-text-body text-sm hover:text-primary transition-colors duration-200 py-2.5 px-3">Log in</Link>
              <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-accent-hover w-full mt-1">
                <Link to="/signup" onClick={() => setMobileOpen(false)}>Sign Up</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default LandingNav;
